import * as React from 'react';
import { Grid, Button, Alert, TextLink, Loader, TextPassage, WarningIcon, ExclamationFilledIcon } from 'react-unity';
import RequestFormWrapper from '../common/wrappers/RequestFormWrapper';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import TextAreaField from '../common/form-controls/TextareaField';
import { OnPremPORTypeInput } from './components/OnPremPORTypeInput';
import { OnPremRulesTable } from './components/OnPremRulesTable';
import TextInputField from '../../components/common/form-controls/TextInputField';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import {
	OnPremAbstractPORForm,
	AbstractPORFormState,
	AbstractPORFormProps,
	IPORStateForm,
} from './OnPremAbstractPORForm';
import { Validation } from '../../utils/Validation';
import AlertModal from '../common/modals/AlertModal';
import SubmitPortOpeningRequestOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/SubmitPortOpeningRequestOnPrem';
import SubmitPortOpeningRequestRuleOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/SubmitPortOpeningRequestRuleOnPrem';
import ConfirmModal from '../common/modals/ConfirmModal';
import { RuleOperation } from '../../models/enums/POR/RuleOperation';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactButton from '../common/ReactButton';
import PortOpeningRuleOnPrem from '../../models/entities/PortOpeningRuleOnPrem';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import { ApproverInput } from '../PortOpeningRequests/components';
import OnPremPortOpeningRequestService from '../../services/OnPremPortOpeningRequestService';
import { OnPremPortOpeningRequestsTypes } from '../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import { OnPremPORScopeInput } from './components/OnPremPORScopeInput';
import SelectField from '../common/form-controls/SelectField';
import {ToggleField} from '../common/';
import './OnPremPortOpeningRequestsCreate.css';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import { authenticatedUser } from '../../authentication/authModule';
import CartaId from '../../models/entities/CartaId';
import OnPremRiskPorts from '../../models/entities/OnPremRiskPorts';

interface PortOpeningRequestsCreateProps extends PortOpeningRequestsCreateWithParamsProps {}

interface PortOpeningRequestsCreateWithParamsProps extends AbstractPORFormProps {
	draftRequest?: PortOpeningRequestOnPrem;
}

interface StateForm extends IPORStateForm {
	selectedType: string;
}

interface ILocationState {
	selectedRules?: PortOpeningRuleOnPrem[];
}

interface PortOpeningRequestsCreateWithParamsState extends AbstractPORFormState<StateForm> {
	cancelModalVisible: boolean;
	changeTypeModalVisible: boolean;
	newType: string;
	loading: boolean;
	subToInternetWarningVisible: boolean;
	selectedFromExisting: string[];
	risksPorts: OnPremRiskPorts;
	riskPortAlerts: { hasCritical: boolean; hasHigh: boolean };
}

class PortOpeningRequestsCreateWithParams extends OnPremAbstractPORForm<
	PortOpeningRequestsCreateWithParamsProps,
	PortOpeningRequestsCreateWithParamsState
> {
	OnPremPortOpeningRequestService: OnPremPortOpeningRequestService;

	constructor(props) {
		super(props);
		this.OnPremPortOpeningRequestService = new OnPremPortOpeningRequestService();
		this.state = {
			stateForm: {
				selectedType: '',
				contact: null,
				trackerID: null,
				firewall: '',
				cartaId: [],
				appEndorser: null,
				businessJustification: '',
				ruleList: [],
				ownerDelegate: null,
				hasRiskPorts: false,
			},
			riskPortAlerts: { hasCritical: false, hasHigh: false },
			endModal: {
				visible: false,
			},
			submissionAlert: {
				visible: false,
			},
			submissionWarned: false,
			operationInProgress: false,
			cancelModalVisible: false,
			changeTypeModalVisible: false,
			newType: null,
			implementedRules: [],
			loading: true,
			subToInternetWarningVisible: true,
			loadingImplementedRules: true,
			failedImplementedRules: false,
			loadingTracker: false,
			failedTracker: false,
			selectedFromExisting: [],
			risksPorts: null,
		};
		this.initFormModel();
	}

	initFormModel() {
		super.initFormModel();

		this.formModel.addField('type', {
			getValue: () => this.state.stateForm.selectedType,
			validation: {
				required: true,
			},
		});

		this.formModel.addField('hasRiskPorts', {
			getValue: () => this.state.stateForm.hasRiskPorts,
		});

		this.formModel.fields.trackerID.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
			rules: [
				{
					assert: () => /^[0-9]*$/.test(this.state.stateForm.trackerID?.toString() ?? ''),
					message: 'Only numbers are valid values',
				},
				{
					assert: () => this.state.stateForm.trackerID >= 10000,
					message: 'Tracker ID must be greater than 10000',
				},
			],
		});

		this.formModel.fields.cartaId.validation = new Validation({
			required: () => this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name,
		});

		this.formModel.fields.appEndorserId.validation = new Validation({
			required: () => this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name,
		});

		// this.formModel.fields.approverId.validation = new Validation({
		// 	required: () => this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name,
		// });

		this.formModel.fields.ownerDelegate.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
		});
		this.formModel.fields.firewall.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
		});

		this.formModel.fields.firewallEnvironment.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
		});

		this.formModel.fields.portOpeningRules.getValue = () =>
			this.state.stateForm.ruleList.map((rule) => new SubmitPortOpeningRequestRuleOnPrem(rule));

		this.formModel.addField('onPremPortOpeningRequestId', {
			getValue: () => this.props.draftRequest?.id,
		});
	}

	stateFormFromRequest(por: PortOpeningRequestOnPrem): StateForm {
		let obj = {
			...super.stateFormFromRequest(por),
			selectedType: por.type?.name || '',
		};
		return {
			...obj,
			ruleList: obj.ruleList.map((rule) => {
				return { ...rule };
			}),
		};
	}

	async componentDidMount() {
		window.scroll(0, 0);
		try {
			if (this.props.draftRequest) {
				this.setState({ stateForm: this.stateFormFromRequest(this.props.draftRequest) });
				if (this.props.draftRequest.type == OnPremPortOpeningRequestsTypes.InternalCertConnection) {
					await this.handleApproverDelegateOptions(this.props.draftRequest.trackerID);
				} else if (this.props.draftRequest.type == OnPremPortOpeningRequestsTypes.InternetDMZ) {
					await this.handleAppEndorserOptions(this.props.draftRequest?.cartaId[0]?.cartaAppId);
				}
			}
			if (this.props.location.state) {
				const { selectedRules }: ILocationState = this.props.location.state;
				this.handleStateFormChange('selectedType', selectedRules[0].type.name);
				this.handleStateFormChange(
					'ruleList',
					selectedRules.map((rule) => {
						return { ...rule, operation: RuleOperation.Modify };
					})
				);
				this.setState({
					selectedFromExisting: selectedRules.map((rule) => rule.name),
				});
			}

			const risksPorts = await this.getRisksPorts();

			this.setState({
				loading: false,
				risksPorts,
			});
		} catch (err) {
			this.setSubmissionAlertError(
				err?.response?.data?.message ??
					'An error occured when processing your request. Please, try again later.'
			);
		}

		if (
			this.props.draftRequest &&
			this.props.draftRequest.cartaId != null &&
			Array.isArray(this.props.draftRequest.cartaId) &&
			this.props.draftRequest.cartaId[0].cartaAppId != null
		)
			this.getCartaIdName(this.props.draftRequest.cartaId[0].cartaAppId);
	}

	returningRoute = () => {
		return `/OnPremPortOpeningRequests`;
	};

	handleCancel = () => {
		this.setState({
			cancelModalVisible: false,
			operationInProgress: false,
		});
		this.props.navigate('/onPremPortOpeningRequests');
	};

	handleSubmit = async () => {
		this.setSubmissionAlertInProgress('Submitting your request...');
		try {
			if (this.requiresDOAGApproval(this.state.stateForm.selectedType)) {
				const response = await this.verifyApproverLevel(this.state.stateForm?.selectedType);
				if (response && !response.allowed) {
					this.setState({
						operationInProgress: false,
						submissionWarned: true,
						submissionAlert: {
							visible: true,
							text: 'The Approver does not meet the minimum DOAG level required.',
							variant: 'warning',
						},
					});
					return;
				}
			} 
			let model: SubmitPortOpeningRequestOnPrem;
			model = this.formModel.create(SubmitPortOpeningRequestOnPrem);
			let request: PortOpeningRequestOnPrem;
			request = await this.portOpeningRequestServiceOnPrem.create(model);
			this.handleEndModal(`Your request was submitted successfully.`, 'success', 5000, this.returningRoute());
		} catch (err) {
			this.setSubmissionAlertError(
				err?.response?.data?.message ??
					'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
			});
		}
	};

	handleUpdate = async () => {
		this.setSubmissionAlertInProgress('Saving changes...');
		try {
			const model = this.formModel.create(SubmitPortOpeningRequestOnPrem);
			const request = await this.OnPremPortOpeningRequestService.saveDraft(model);
			this.handleEndModal(
				`Request #${request.id} was saved successfully.`,
				'success',
				5000,
				this.returningRoute()
			);
		} catch (err) {
			this.setSubmissionAlertError(
				err?.body?.message ?? 'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
			});
		}
	};

	handleScopeChange = async (event: any) => {
		if (this.state.stateForm.ruleList.length > 0) {
			this.setState({ changeTypeModalVisible: true, newType: event.target.value });
		} else {
			this.handleStateFormChange('selectedType', event.target.value);
			this.handleStateFormChange('direction', null);
			this.handleStateFormChange('trackerID', null);
			this.handleStateFormChange('firewall', null);
			this.handleStateFormChange('approverDOAG', null);
			this.handleStateFormChange('cartaId', []);
			this.handleStateFormChange('appEndorser', null);
			this.handleStateFormChange('approverDelegate', []);
			await this.setImplementedRules();
		}
	};

	handleDirectionChange = async (event: any) => {
		this.stateFormHandler().direction.onChange(event);
		await this.setImplementedRules();
	};

	handleTrackerIdChange = async (event: any) => {
		this.stateFormHandler().trackerID.onChange(event);
	};

	handleCartaIdChange = async (cartaInput: CartaId[]) => {
		this.stateFormHandler().cartaId.onChange(cartaInput);
		await this.setImplementedRules();
	};

	confirmTypeChange = async () => {
		this.handleStateFormChange('selectedType', this.state.newType);
		this.handleStateFormChange('direction', null);
		this.handleStateFormChange('ruleList', []);
		this.handleStateFormChange('trackerID', null);
		this.handleStateFormChange('firewall', null);
		this.handleStateFormChange('approverDOAG', null);
		this.handleStateFormChange('approverDelegate', []);
		this.handleStateFormChange('cartaId', []);
		this.handleStateFormChange('appEndorser', null);
		this.setState({
			changeTypeModalVisible: false,
			newType: null,
		});
		await this.setImplementedRules();
	};

	ruleIsFromSelectedDirection(rule: PortOpeningRuleOnPrem): boolean {
		return rule.direction?.name === this.state.stateForm.direction;
	}

	ruleIsAddedAlready(rule: PortOpeningRuleOnPrem): boolean {
		return this.state.stateForm.ruleList.map((editedRule) => editedRule.azureName).includes(rule.azureName);
	}

	getImplementedRulesToShow(): PortOpeningRuleOnPrem[] {
		return this.state.implementedRules.filter(
			(rule) =>
				this.ruleIsFromSelectedDirection(rule) &&
				!this.ruleIsAddedAlready(rule) &&
				!this.state.selectedFromExisting.includes(rule.name)
		);
	}

	setSelectedFromExisting = (rulesName: string[]) => {
		this.setState({
			selectedFromExisting: rulesName,
		});
	};

	isValid = () => {
		return this.formModel.isValid() && (this.props.draftRequest || !this.wasNotEdited());
	};

	isKeepOrRemove = (rule) => rule.operation == RuleOperation.Keep || rule.operation == RuleOperation.Remove;

	/*attestationIsValid = () => {
		return this.props.draftRequest?.isAttestation &&
			this.state.stateForm.ruleList.every(this.isKeepOrRemove)
			field.validation.assert(field.getValue())
	}*/

	wasNotEdited = () => {
		return this.state.stateForm.ruleList.some((rule) => rule.azureName != undefined);
	};

	render() {
		return (
			<>
				<RequestFormWrapper
					title={'New Port Opening Request'}
					linkTree={this.getLinkTree()}
					loading={this.state.loading}
				>
					<p>
						For more information about PORs{' '}
						<TextLink
							className="em-u-margin-right-none"
							external
							target="_blank"
							href="https://fi-gw-policy-portal-git-fi-gateway-policy.apps.rmn01.ocp.na.xom.com/por_landing"
						>
							Click here
						</TextLink>
					</p>
					{this.state.submissionAlert.visible && (
						<Alert variant={this.state.submissionAlert.variant} onClose={this.handleSubmissionAlertClose} className='onPrem-Alert'>
							{this.state.submissionAlert.text}
						</Alert>
					)}
					<Grid variant="halves">
						<Grid.Item>
							<OnPremPORTypeInput
								type={{
									value: this.state.stateForm.selectedType,
									validation: this.formModel.fields.type.validation,
									onChange: this.handleScopeChange,
									disabled: this.state.operationInProgress,
								}}
							/>
						</Grid.Item>
					</Grid>
					<Grid variant="halves">
						<Grid.Item>
							<OnPremPORScopeInput
								direction={{
									...this.stateFormHandler().direction,
									onChange: this.handleDirectionChange,
								}}
								type={this.state.stateForm.selectedType}
							/>
						</Grid.Item>
					</Grid>
					{this.requiresApproverVerification(this.state.stateForm?.selectedType) && (
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Tracker Id"
									{...this.stateFormHandler().trackerID}
									disabled={this.state.operationInProgress}
									//scope={this.state.stateForm?.selectedType}
									readOnly={this.state.loadingTracker}
									onChange={this.handleTrackerIdChange}
									note={
										this.state.loadingTracker
											? "Validating..."
											: this.state.failedTracker
											? this.state.errorTracker
											: ""
									}
									/* onBlur={ ()=>{
									if (!(this.formModel.fields.trackerID.validation.rules.filter(x=>x.assert()===false).length>0) && !this.state.loadingTracker)
										this.handleApproverDelegateOptions(this.state.stateForm.trackerID)
									}
								} */
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									label="Firewall"
									className="full-width"
									{...this.stateFormHandler().firewall}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().firewall.options.length == 0 ||
										this.state.loadingTracker
									}
									isLoading={this.stateFormHandler().firewall.options.length == 0}
									note={
										this.stateFormHandler().firewall.options.length > 0 ? (
											<></>
										) : (
											<span id="firewallError">
												No NCP is linked to the Tracker ID in NCDB, please contact
												GSC-EMIT-NETSEC-RCS /SM to help remediate
											</span>
										)
									}
								/>
							</Grid.Item>
						</Grid>
					)}
					{this.requiresAppEndorserApproval(this.state.stateForm?.selectedType) && (
						<Alert variant="default">
							<ul className="em-c-bulleted-list">
								<li className="em-c-bulleted-list__item">
									Approver is being pulled from Ardoq based on the App-ID.{' '}
								</li>
								<li className="em-c-bulleted-list__item">
									Eligible approvers are Business Owner, IT Owner, and IT Architect who are active employees.{' '}
									<TextLink
										className="em-u-margin-right-none"
										external
										target="_blank"
										href="https://goto/ARDOQ"
									>
										goto/ARDOQ
									</TextLink>
									{' '} to update role assignments.
								</li>
							</ul>
						</Alert>
					)}
					<Grid variant="2-up">
						<Grid.Item>
							{this.requiresDOAGApproval(this.state.stateForm?.selectedType) && (
								<>
									<ApproverInput
										{...this.stateFormHandler().approverDOAG}
										disabled={this.state.operationInProgress}
										onChange={(selectedApprover) => {
											if (selectedApprover?.id === authenticatedUser.userId) {
												this.setState({
													submissionAlert: {
														visible: true,
														text: 'You cannot select yourself as the DOAG approver.',
														variant: 'warning',
													},
												});
											} else {
												this.setState({
													submissionAlert: {
														visible: false,
													},
												});
											}
											this.stateFormHandler().approverDOAG.onChange(selectedApprover);
										}}
									/>
								</>
							)}
							{this.requiresApproverVerification(this.state.stateForm?.selectedType) && (
								<SelectField
									label="Approver"
									className="full-width"
									{...this.stateFormHandler().ownerDelegate}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().ownerDelegate.options.length == 0 ||
										this.state.loadingTracker
									}
									isLoading={this.stateFormHandler().ownerDelegate.options.length == 0}
									note={
										this.stateFormHandler().ownerDelegate.options.length > 0 ? (
											<></>
										) : (
											<span id="firewallError">
												Approver is being pulled from NCDB based on the Tracker ID and active employee status. goto/NCDB to update role assignments.
											</span>
										)
									}
								/>
							)}
						</Grid.Item>
						<Grid.Item>
							<UserLookup
								label="Technical contact"
								{...this.stateFormHandler().contact}
								disabled={this.state.operationInProgress}
							/>
						</Grid.Item>
					</Grid>
					{this.requiresAppEndorserApproval(this.state.stateForm?.selectedType) && (
						<Grid variant="2-up">
							<Grid.Item>
								<CartaInput
									{...this.stateFormHandler().cartaId}
									disabled={this.state.operationInProgress}
									oneOptionOnly={this.state.stateForm.cartaId.length > 0}
									hideNotes={this.state.operationInProgress}
									onChange={this.handleCartaIdChange}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									label="Approver (Business Owner, IT Owner, IT Architect)"
									className="full-width"
									{...this.stateFormHandler().appEndorser}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().appEndorser.options.length == 0
									}
									isLoading={this.stateFormHandler().appEndorser.options.length == 0}
									note={
										this.stateFormHandler().appEndorser.options.length == 0 &&
										'First select the DBM'
									}
									/>
							</Grid.Item>
							{this.state.stateForm?.selectedType == OnPremPortOpeningRequestsTypes.InternetDMZ.name &&
								this.state.stateForm?.direction ==
									PortOpeningRequestsDirection.iBossProxyToInternet.name && (
									<Alert variant="default">
										<ul className="em-c-bulleted-list">
											<li className="em-c-bulleted-list__item">
												APP ID: Please select the end application that this rule is
												enabling/supporting. If you feel that you are not required to have an
												app-id for this rule to be enabled AND you can complete your business
												need with the rule temporarily in place for 30 days, please contact the
												GSC-EMIT-NETSEC-RCS mailbox to discuss.
											</li>
										</ul>
									</Alert>
								)}
						</Grid>
					)}
					{this.requiresApproverVerification(this.state.stateForm?.selectedType) && (
						<Grid variant="2-up">
							<Grid.Item>
								<ToggleField
									{...this.stateFormHandler().siteCoordination}
									id="siteCoordination"
									label="Site Coordination Required"
									options={[
										{ value: 'true', text: 'Yes' },
										{ value: 'false', text: 'No' },
									]}
									disabled={this.state.operationInProgress}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									label="Firewall Environment"
									className="full-width"
									{...this.stateFormHandler().firewallEnvironment}
									disabled={this.state.operationInProgress}
								/>
							</Grid.Item>
						</Grid>
					)}
					<TextAreaField
						label="Business Justification"
						{...this.stateFormHandler().businessJustification}
						disabled={this.state.operationInProgress}
						note={''}
					/>
					{this.assertIpsContainLargeSubnets() ? (
						<Alert variant="warning">
							Large subnets have been detected. Please provide a detailed Business justification for their
							use.
						</Alert>
					) : (
						<></>
					)}
				{this.state.riskPortAlerts.hasHigh && (
					<Alert variant="warning">The POR contains High Risk Ports</Alert>
				)}
				{this.state.riskPortAlerts.hasCritical && (
					<Alert variant="error">The POR contains Critical Risk Ports</Alert>
				)}
				<OnPremRulesTable
					{...this.stateFormHandler().ruleset}
					porType={new OnPremPortOpeningRequestsTypes().fromName(this.state.stateForm.selectedType)}
					implementedRules={this.getImplementedRulesToShow()}
					loadingImplementedRules={this.state.loadingImplementedRules}
					failedImplementedRules={this.state.failedImplementedRules}
					selectedFromExisting={this.state.selectedFromExisting}
					setSelectedFromExisting={this.setSelectedFromExisting}
					direction={new PortOpeningRequestsDirection().fromName(this.state.stateForm.direction)}
					risksPorts={this.state.risksPorts}
					hasRiskPorts={this.handleStateFormChange.bind(this, 'hasRiskPorts')}
					onRiskTypeChange={(types) => this.setState({ riskPortAlerts: types })}
					/>
					<Grid variant="4-up">
						<Grid.Item>
							<Button
								variant="secondary"
								onClick={() => {
									this.setState({
										cancelModalVisible: true,
										operationInProgress: true,
									});
								}}
								disabled={this.state.operationInProgress}
							>
								Cancel Request
							</Button>
						</Grid.Item>
						<Grid.Item />
						<Grid.Item>
							<ReactButton
								variant="primary"
								disabled={!this.formModel.isValid() || this.state.operationInProgress}
								isLoading={this.state.operationInProgress}
								handleUpdateSubmit={this.handleUpdate}
								loadingVariant="secondary"
								name="Save & Close"
							/>
						</Grid.Item>
						<Grid.Item>
							<ReactButton
								variant="primary"
								disabled={!this.isValid()}
								isLoading={this.state.operationInProgress}
								handleUpdateSubmit={this.handleSubmit}
								loadingVariant="secondary"
								name="Submit Request"
							/>
						</Grid.Item>
					</Grid>
				</RequestFormWrapper>
				<ConfirmModal
					visible={this.state.cancelModalVisible}
					title="Cancel Request?"
					question="Are you sure you want to cancel this request? This action cannot be undone."
					confirmButton={{
						label: 'Yes',
						props: {
							variant: 'primary',
							color: 'negative',
						},
					}}
					cancelButton={{
						label: 'No',
					}}
					onConfirm={this.handleCancel}
					onCancel={() => {
						this.setState({
							cancelModalVisible: false,
							operationInProgress: false,
						});
					}}
				/>
				<ConfirmModal
					visible={this.state.changeTypeModalVisible}
					title="Change Type"
					question="Are you sure you want to continue? If so, the ruleset will be cleared."
					confirmButton={{
						label: 'Yes',
					}}
					onConfirm={this.confirmTypeChange}
					onCancel={() => {
						this.setState({
							changeTypeModalVisible: false,
						});
					}}
				/>
				<AlertModal {...this.state.endModal} willTimeout={false} onClose={this.endAndRedirect} />
			</>
		);
	}
}

const OnPremPortOpeningRequestsCreate = (props: PortOpeningRequestsCreateProps) => {
	return (
		<PortOpeningRequestsCreateWithParams
			draftRequest={props.draftRequest}
			location={useLocation()}
			navigate={useNavigate()}
			params={useParams()}
		/>
	);
};

export default OnPremPortOpeningRequestsCreate;
